import { Box, Flex, Text } from '@common/fundamentals'
import {
  useGetNotifications,
  useMarkAllNotificationsAsRead,
  useMarkAllNotificationsAsSeen,
} from '@core/react-query/features/notifications'
import { useGetProfile } from '@core/react-query/features/profile'
import { useToast } from '@core/toast'
import { useEffect, useMemo } from 'react'
import _flatten from 'lodash/flatten'
import { NotificationsListItem } from './NotificationListItem'
import InfiniteScroll from 'react-infinite-scroll-component'
import { localizedStrings } from '@core/strings'
import { PageTitle } from '@elements'
import theme from 'styles/theme'
import { Palette } from '@core/theme/color-palette-siteassist'
import {
  Clear,
  DoneAllOutlined,
  InboxOutlined,
  MoreHorizOutlined,
  NotificationsOutlined,
} from '@mui/icons-material'
import { CircularProgress, DropdownButton, IconButton } from '@common/material'
import { useDrawer } from '@common/GlobalDrawer'
import { useAppNotificationGetStatus } from '@modules/notifications'
import { TransitionGroup } from 'react-transition-group'
import { Collapse } from '@mui/material'
import { useAnalytics } from '@common/hooks'
import { useGetNotificationsUnseenCounter } from '@core/react-query/features/notifications'

interface NotificationsListProps {
  pageSize?: number
  onClose?: () => void
}

const HEADER_ACTION_SX = {
  width: 24,
  height: 24,
  borderRadius: '4px',
  '&:hover': { bgcolor: Palette.Grey[200] },
} as const

export const NotificationsList = ({
  pageSize = 25,
  onClose = () => {},
}: NotificationsListProps) => {
  const toast = useToast()
  // TODO: This should be removed when old layout no longer used, only onClose should fire
  const { hideDrawer } = useDrawer()
  const { sendAnalyticsEvent } = useAnalytics()
  const { data: profile } = useGetProfile()
  const { status: filter, setStatus: setFilter } = useAppNotificationGetStatus()

  const { mutate: markAllNotificationsAsSeen } = useMarkAllNotificationsAsSeen()
  const { mutate: markAllNotificationsAsRead } = useMarkAllNotificationsAsRead()

  const {
    data: notificationsData,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useGetNotifications(
    { userId: profile?.id, status: filter, size: pageSize },
    {
      enabled: !!profile,
      onError: (error) => {
        toast.error(String(error?.message))
      },
    },
  )

  const { data: unseenCounterData } = useGetNotificationsUnseenCounter(
    { userId: profile?.id },
    { enabled: !!profile },
  )

  const flattenNotifications = useMemo(
    () => _flatten(notificationsData?.pages),
    [notificationsData?.pages],
  )

  const unreadNotifications = useMemo(
    () => flattenNotifications.filter((notification) => !notification.isRead),
    [flattenNotifications],
  )

  const notifications = useMemo(() => {
    const filteredNotifications =
      filter === 'UNREAD' ? unreadNotifications : flattenNotifications

    return filteredNotifications
  }, [unreadNotifications, filter])

  const numberOfNotifications =
    (notificationsData?.pages.length - 1) * pageSize +
      notificationsData?.pages.slice(-1)[0].length || 0

  useEffect(() => {
    markAllNotificationsAsSeen({ userId: profile?.id })
  }, [])

  const handleMarkAllAsRead = () => {
    // Send analytics event
    sendAnalyticsEvent({
      eventName: 'notification.mark-all-as-read',
      eventProperties: {
        platform: 'web',
      },
    })

    // Mark all notifications as read
    markAllNotificationsAsRead({ userId: profile.id })
  }

  return (
    <Box
      width='27rem'
      overflow='hidden'
      bgcolor='#FAFAFA'
      height='100%'
      display='flex'
      flexDirection='column'
    >
      <Flex
        py={1}
        pl={3}
        pr={2}
        justifyContent='space-between'
        alignItems='center'
        bgcolor='white'
        sx={{
          height: theme.variants.newNavbar.height,
          borderBottom: '0.5px solid',
          borderColor: 'divider',
        }}
      >
        <Flex alignItems='center' gap={2}>
          <PageTitle
            title={localizedStrings.notifications}
            sx={{ mb: 0, fontSize: 14, fontWeight: 500 }}
          />
          {isFetching && <CircularProgress size={18} />}
        </Flex>
        <Flex gap={0.5}>
          <DropdownButton
            label={localizedStrings.actions}
            compactMenu
            options={[
              {
                title: localizedStrings.readAndUnread,
                onSelect: () => setFilter('ALL'),
                sectionId: 1,
                selected: filter === 'ALL',
                icon: <InboxOutlined />,
              },
              {
                title: localizedStrings.unreadOnly,
                onSelect: () => setFilter('UNREAD'),
                sectionId: 1,
                selected: filter === 'UNREAD',
                icon: <NotificationsOutlined />,
              },
              {
                title: localizedStrings.markAllAsRead,
                onSelect: handleMarkAllAsRead,
                sectionId: 2,
                icon: <DoneAllOutlined />,
              },
            ]}
            sections={[
              {
                id: 1,
                title: localizedStrings.filter,
              },
              {
                id: 2,
                title: localizedStrings.actions,
              },
            ]}
            renderButton={(onClick) => (
              <IconButton size='small' onClick={onClick} sx={HEADER_ACTION_SX}>
                <MoreHorizOutlined sx={{ width: 16, height: 16 }} />
              </IconButton>
            )}
          />

          <IconButton
            size='small'
            sx={HEADER_ACTION_SX}
            onClick={() => {
              hideDrawer()
              onClose?.()
            }}
          >
            <Clear sx={{ width: 16, height: 16 }} />
          </IconButton>
        </Flex>
      </Flex>

      <Box
        id='scrollableContainer'
        sx={{ overflow: 'scroll', flex: 1, minHeight: 0 }}
      >
        <InfiniteScroll
          dataLength={numberOfNotifications}
          next={fetchNextPage}
          hasMore={hasNextPage}
          loader={
            <Text mt={1.5} textAlign='center' color='grey'>
              {localizedStrings.loading}
            </Text>
          }
          scrollableTarget='scrollableContainer'
        >
          <TransitionGroup>
            {notifications.map((notification) => (
              <Collapse key={notification.id}>
                <NotificationsListItem
                  notification={notification}
                  onItemClick={onClose}
                />
              </Collapse>
            ))}
            {notifications.length === 0 && (
              <Collapse>
                <Text mt={1.5} textAlign='center' color='grey'>
                  {localizedStrings.youAreUpToDate}
                </Text>
              </Collapse>
            )}
          </TransitionGroup>
        </InfiniteScroll>
      </Box>
    </Box>
  )
}
